import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { api, getListItems } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import { stripHtml, socialFromUrl } from '../../utils/text';
import { useSiteContent, sectionField } from '../../hooks/useSiteContent';

const DEFAULT_AVATAR = '/assets/img/testimonial/testi_1_1.jpg';

function avatarSrc(image) {
  return getImageUrl(image, 'testimonial') || DEFAULT_AVATAR;
}

function handleAvatarError(event) {
  const img = event.currentTarget;
  if (img.dataset.fallback === '1') {
    return;
  }
  img.dataset.fallback = '1';
  img.src = DEFAULT_AVATAR;
}

function TestimonialOne() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const { content } = useSiteContent('home');
  const subtitle = sectionField(content, 'testimonials', 'subtitle', 'Testimonial');
  const title = sectionField(content, 'testimonials', 'title', 'What Clients Say About Us');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getTestimonials();
        const items = getListItems(res).map((item) => {
          const text = stripHtml(item.testimonial || item.content || item.text || '');
          const social = socialFromUrl(item.video_url || item.videoUrl || '');
          return {
            name: item.client_name || item.name || 'Guest',
            designation: item.client_title || item.designation || 'Traveller',
            image: avatarSrc(item.client_image || item.image),
            text,
            rating: Number(item.rating) || 5,
            social,
          };
        }).filter((item) => item.text);
        if (!cancelled) setTestimonials(items);
      } catch {
        if (!cancelled) setTestimonials([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loading && testimonials.length === 0) {
    return null;
  }

  return (
    <section className="testi-area overflow-hidden space shape-mockup-wrap" id="testi-sec">
      <div className="container-fluid p-0">
        <div className="title-area mb-20 text-center">
          <span className="sub-title">{subtitle}</span>
          <h2 className="sec-title">{title}</h2>
        </div>
        {loading ? (
          <p className="text-center">Loading testimonials…</p>
        ) : (
          <div className="slider-area">
            <Swiper
              modules={[Pagination, Navigation]}
              pagination={{ clickable: true }}
              spaceBetween={30}
              centeredSlides={true}
              loop={testimonials.length > 2}
              slidesPerGroup={1}
              speed={1200}
              breakpoints={{
                0: { slidesPerView: 1 },
                767: { slidesPerView: 2 },
                992: { slidesPerView: 2 },
                1200: { slidesPerView: 2 },
                1400: { slidesPerView: 3 },
              }}
              className="testiSlider1 has-shadow"
            >
              {testimonials.map((item, index) => (
                <SwiperSlide key={index}>
                  <div className="testi-card">
                    <div className="testi-card_wrapper">
                      <div className="testi-card_profile">
                        <div className="testi-card_avater">
                          <img
                            src={item.image}
                            alt={item.name}
                            onError={handleAvatarError}
                          />
                        </div>
                        <div className="media-body">
                          <h3 className="box-title">{item.name}</h3>
                          <span className="testi-card_desig">{item.designation}</span>
                        </div>
                      </div>
                      <div className="testi-card_review">
                        {[...Array(Math.min(5, Math.max(1, item.rating)))].map((_, i) => (
                          <i key={i} className="fa-solid fa-star" />
                        ))}
                        {item.social && (
                          <a
                            className="testi-card_social"
                            href={item.social.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={item.social.label}
                            title={item.social.label}
                          >
                            <i className={item.social.icon} />
                          </a>
                        )}
                      </div>
                    </div>
                    <p className="testi-card_text">{item.text}</p>
                    <div className="testi-card-quote">
                      <img src="/assets/img/icon/testi-quote.svg" alt="" />
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="slider-pagination" />
          </div>
        )}
      </div>
      <div className="shape-mockup d-none d-xl-block" style={{ bottom: '-2%', right: '0%' }}>
        <img src="/assets/img/shape/line2.png" alt="" />
      </div>
      <div className="shape-mockup movingX d-none d-xl-block" style={{ top: '30%', left: '5%' }}>
        <img src="/assets/img/shape/shape_7.png" alt="" />
      </div>
    </section>
  );
}

export default TestimonialOne;
